<script>
    import { onMount } from "svelte";

    let activeStep = 0;

    onMount(() => {
        const interval = setInterval(() => {
            activeStep = (activeStep + 1) % 3;
        }, 3000);
        return () => clearInterval(interval);
    });

    const steps = [
        {
            title: "1. Define",
            subtitle: "Describe your needs in plain English",
            icon: "M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z",
            colorClass: "text-blue-400",
            bgClass: "bg-blue-500/10",
            borderClass: "border-blue-500/20",
            indicatorClass: "via-blue-400",
        },
        {
            title: "2. Architect",
            subtitle: "AI generates complete system design",
            icon: "M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z",
            colorClass: "text-purple-400",
            bgClass: "bg-purple-500/10",
            borderClass: "border-purple-500/20",
            indicatorClass: "via-purple-400",
        },
        {
            title: "3. Deploy",
            subtitle: "Production-ready infrastructure instantly",
            icon: "M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01",
            colorClass: "text-emerald-400",
            bgClass: "bg-emerald-500/10",
            borderClass: "border-emerald-500/20",
            indicatorClass: "via-emerald-400",
        },
    ];
</script>

<section class="py-24 bg-[#0a0a0f] relative overflow-hidden">
    <!-- Connecting Line Background -->
    <div
        class="absolute top-1/2 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-y-1/2 hidden md:block"
    ></div>

    <div class="container mx-auto px-6 relative z-10">
        <div class="text-center mb-20">
            <h2 class="text-3xl md:text-5xl font-bold text-white mb-6">
                How It Works
            </h2>
            <p class="text-gray-400 max-w-2xl mx-auto text-lg">
                From concept to cloud in minutes, not months.
            </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {#each steps as step, i}
                <div
                    class="relative transition-all duration-500 transform"
                    class:scale-105={activeStep === i}
                    class:opacity-100={activeStep === i}
                    class:opacity-60={activeStep !== i}
                >
                    <div
                        class="relative z-10 rounded-2xl bg-[#13131f] border {step.borderClass} p-8 h-full backdrop-blur-sm transition-all duration-300 hover:border-opacity-100 hover:shadow-[0_0_30px_rgba(0,0,0,0.5)] flex flex-col items-center text-center group"
                    >
                        <!-- Icon Circle -->
                        <div
                            class="w-20 h-20 rounded-full {step.bgClass} {step.colorClass} flex items-center justify-center mb-6 relative group-hover:scale-110 transition-transform duration-300"
                        >
                            {#if activeStep === i}
                                <div
                                    class="absolute inset-0 rounded-full border border-current animate-ping opacity-20"
                                ></div>
                            {/if}
                            <svg
                                class="w-10 h-10"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="1.5"
                                    d={step.icon}
                                />
                            </svg>
                        </div>

                        <!-- Content -->
                        <h3 class="text-2xl font-bold text-white mb-3">
                            {step.title}
                        </h3>
                        <p class="text-gray-400 leading-relaxed">
                            {step.subtitle}
                        </p>

                        <!-- Active Indicator -->
                        {#if activeStep === i}
                            <div
                                class="mt-8 h-1 w-24 rounded-full bg-gradient-to-r from-transparent {step.indicatorClass} to-transparent"
                            ></div>
                        {/if}
                    </div>
                </div>
            {/each}
        </div>
    </div>
</section>
